import type { FC } from 'react'
import { useMemo, useState, lazy, Suspense } from 'react'

import {
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
} from '@/components/interactives/dropdown-menu'
import {
  faCog,
  faPlus,
  faArrowUp,
  faArrowDown,
  faLeftRight,
  faPalette,
  faCopy,
  faTrashAlt,
  faForwardStep,
  faArrowRightLong,
  faCode,
} from '@fortawesome/free-solid-svg-icons'

import { cn, truncateText } from '@/utils'
import copy from 'copy-to-clipboard'
import { useSetAtom } from 'jotai'

import { arrayIncludes } from '@/utils/primitives'

import { CUE_BACKGROUND_COLORS } from '~shared/consts'
import { CueType } from '~shared/rundown/cue'
import type { Cue } from '~shared/rundown/cue'
import { RundownPermission } from '~shared/rundown/permission'

import { AddRundownCuePosition } from '@/entities/rundown'
import { toastAtom } from '@/store/toast.store'

import { useModal } from '@/features/modal-manager'
import { useRundownPermission } from '@/features/permissions/lib/use-rundown-permission'
import {
  useIsCueSelected,
  useSelectedCueIds,
} from '@/features/rundown/cue/cue-ui.store'

import { useRundown, useRundownId } from '../../rundown.derived'
import { CueColorPicker } from '../CueColorPicker'

import { CueSettingsSubmenu } from './CueSettingsSubmenu'
import type { AddCueCallback, UpdateCueCallback } from './menu.types'

const ModalWrapper = lazy(() => import('@/components/modal/ModalWrapper'))
const RundownColoursModal = lazy(
  () => import('@/components/modal/RundownColoursModal.jsx'),
)

type CueSettingsMenuProps = {
  cue: Cue
  parentId?: string | null

  onAddCue: AddCueCallback
  onUpdateCue: UpdateCueCallback
  onDuplicateCue: () => void
  onDelete: () => void
  onJumpToCue: () => void
  onSetNext: () => void
}

export const CueSettingsMenu: FC<CueSettingsMenuProps> = ({
  cue,
  parentId,
  onAddCue,
  onUpdateCue,
  onDuplicateCue,
  onDelete,
  onJumpToCue,
  onSetNext,
}) => {
  const [isDeletingGroup, setIsDeletingGroup] = useState(false)

  const rundown = useRundown()
  const rundownId = useRundownId()
  const selectedCues = useSelectedCueIds()
  const addToast = useSetAtom(toastAtom)

  const isCueSelected = useIsCueSelected(cue.id)
  const isParentCueSelected = useIsCueSelected(parentId)
  const hasShowCallPermission = useRundownPermission({
    permission: RundownPermission.SHOWCALL,
  })

  const skipDuringShow = cue.settings.skipDuringShow

  const isCueSchedulingDisabled = useMemo(() => {
    return (
      arrayIncludes([CueType.HEADING, CueType.GROUP], cue.type) ||
      !hasShowCallPermission
    )
  }, [cue.type, hasShowCallPermission])

  const [coloursModal, renderColoursModal] = useModal(RundownColoursModal)

  const deleteLabel = useMemo(() => {
    switch (cue.type) {
      case CueType.HEADING: {
        return 'Delete heading'
      }
      case CueType.GROUP: {
        return 'Delete group'
      }
      default: {
        return 'Delete cue'
      }
    }
  }, [cue.type])

  return (
    <>
      <Menu
        icon={faCog}
        disabled={selectedCues.length > 0 || isParentCueSelected}
        className={cn(
          'bg-transparent! h-7! text-base! justify-center! w-full rounded-sm',
          'focus:outline-hidden focus:ring-3',
          'group-hover:enabled:opacity-70 enabled:hover:opacity-100! transition-opacity disabled:hidden',
          parentId ? 'group-hover:opacity-30 opacity-0' : 'opacity-30',
          isCueSelected ? 'opacity-30' : '',
        )}
      >
        <MenuItem
          icon={faForwardStep}
          label="Jump to cue"
          disabled={isCueSchedulingDisabled || skipDuringShow}
          onClick={onJumpToCue}
        />
        <MenuItem
          icon={faArrowRightLong}
          label="Cue up next"
          onClick={onSetNext}
          disabled={isCueSchedulingDisabled || skipDuringShow}
        />
        <MenuSeparator />
        <div className="mb-1">
          <CueColorPicker
            onClick={(color: string) => onUpdateCue({ backgroundColor: color })}
            rundownColors={
              rundown.settings.cueBackgroundColours ?? CUE_BACKGROUND_COLORS
            }
          />
        </div>
        <MenuItem
          icon={faPalette}
          label="Edit rundown colors"
          onClick={coloursModal.show}
        />
        <CueSettingsSubmenu
          cue={cue}
          onUpdateCue={onUpdateCue}
        />
        <MenuSeparator />
        <MenuItem
          icon={faCopy}
          label={`Duplicate ${cue.type === CueType.GROUP ? 'group' : 'cue'}`}
          onClick={onDuplicateCue}
        />
        <Menu
          icon={faPlus}
          label="Add cue..."
        >
          <MenuItem
            icon={faArrowUp}
            label="Add cue above"
            onClick={() => {
              onAddCue({
                params: {
                  [AddRundownCuePosition.BEFORE]: cue.id,
                },
              })
            }}
          />
          <MenuItem
            icon={faArrowDown}
            label="Add cue below"
            onClick={() => {
              onAddCue({
                params: {
                  [AddRundownCuePosition.AFTER]: cue.id,
                },
              })
            }}
          />
        </Menu>
        <Menu
          icon={faLeftRight}
          label="Convert to..."
          disabled={cue.type === CueType.GROUP}
        >
          <MenuItem
            label="Cue"
            active={cue.type === CueType.CUE}
            disabled={cue.type === CueType.CUE}
            onClick={() =>
              onUpdateCue({
                type: CueType.CUE,
              })
            }
          />
          <MenuItem
            label="Heading"
            active={cue.type === CueType.HEADING}
            disabled={cue.type === CueType.HEADING}
            onClick={() =>
              onUpdateCue({
                type: CueType.HEADING,
              })
            }
          />
        </Menu>
        <MenuSeparator />
        <Menu
          icon={faCode}
          label="API"
        >
          <MenuItem
            icon={faCopy}
            label="Copy Cue ID"
            onClick={() => {
              copy(cue.id)
              return addToast({
                title: `Cue ID copied to the clipboard`,
                body: `Cue ID: ${cue.id}`,
                type: 'success',
              })
            }}
          />
          {cue.subTimers.length > 0 && <MenuSeparator />}
          {cue.subTimers.length > 0 && <MenuLabel>Sub-timers</MenuLabel>}
          {cue.subTimers.map((timer, i) => {
            const name = truncateText(timer.name || `Sub-timer ${i + 1}`, 24)
            return (
              <MenuItem
                key={timer.id}
                icon={faCopy}
                label={`Copy "${name}" ID`}
                onClick={() => {
                  copy(timer.id)
                  return addToast({
                    title: 'Sub-timer ID copied to the clipboard',
                    body: `Sub-timer ID: ${timer.id}`,
                    type: 'success',
                  })
                }}
              />
            )
          })}
        </Menu>
        <MenuSeparator />
        <MenuItem
          icon={faTrashAlt}
          label={deleteLabel}
          onClick={() => {
            if (cue.type === CueType.GROUP) {
              setIsDeletingGroup(true)
            } else {
              onDelete()
            }
          }}
        />
      </Menu>
      {isDeletingGroup && (
        <Suspense>
          <ModalWrapper
            open={[isDeletingGroup, setIsDeletingGroup]}
            title="Delete Group"
            buttons={[
              {
                text: 'Cancel',
                onClick: () => setIsDeletingGroup(false),
              },
              {
                text: 'Delete Group',
                icon: faTrashAlt,
                onClick: () => {
                  setIsDeletingGroup(false)
                  onDelete()
                },
              },
            ]}
          >
            <p>
              Deleting this group will permanently remove all cues within it.
            </p>
          </ModalWrapper>
        </Suspense>
      )}

      {renderColoursModal({ rundownId })}
    </>
  )
}
